import React, { useMemo, useRef } from 'react'
import shallow from 'zustand/shallow'
import { getMarginBackground, gridUnit, masksDefaultHeight, masksPosition } from '../../constants/gridConfig'
import { MASKS_TYPE, borderColumnColor, borderColumnErrorColor, containerBackground, maxHeaderFooterHeight, minContainerHeight, pageBackground, viewerTypes } from '../../constants/constants';
import BoxWrapper from '../common/BoxWrapper';
import { Resizable } from 're-resizable';
import { useStore } from '../../store/store';
import { maxContainerHeight } from '../../utils/containers';
import { reinitializeBoxWidthAndHeight } from '../../utils/boxes';
import { columnStackStyle, getColumnAvailableWidth } from '../../utils/columns';
import { getContainerByColumnId, getMaskById } from '../../store/lookups';
import ColumnsRow from './ColumnsRow';
import SnapGuidesOverlay from '../../snapping/SnapGuidesOverlay';
import { isPageBackgroundMask, showsPageBackgroundMargins } from '../../utils/pageBackground'

const selector = (id, mask, columnMaskId, linkedMaskHasNotPageNumber, fromViewer) => ({
  containers,
  updateContainer,
  displayMargins,
  hidePageBackgroundMargins,
  landscape,
  boxVariableSelectedId,
  fromPdf,
  updateMask,
  masks,
  selectedContainerIds,
  selectedContainer
}) => {
  const ownerContainerId = mask ? undefined : getContainerByColumnId(containers, id)?.id;
  let containerHeight;
  if (mask) {
    if (mask.id === MASKS_TYPE.HEADER.id) {
      containerHeight = mask.height ?? masksDefaultHeight.header
    } else if (mask.id === MASKS_TYPE.FOOTER.id) {
      containerHeight = mask.height ?? masksDefaultHeight.footer
    }
  } else {
    containerHeight = getContainerByColumnId(containers, id)?.height
  }
  let hasNotPageNumber;
  if (!mask && columnMaskId) {
    if (typeof linkedMaskHasNotPageNumber !== 'undefined') {
      hasNotPageNumber = linkedMaskHasNotPageNumber
    }
    else {
      hasNotPageNumber = !!getContainerByColumnId(containers, id)?.KeyPdfToImage || getMaskById(masks, columnMaskId)?.hasNotPageNumber
    }
  }
  return {
    containerId: ownerContainerId,
    // Espacement propre au container (panneau de disposition), undefined tant
    // qu'il n'a pas été réglé : la valeur du document s'applique alors.
    containerLineSpacing: getContainerByColumnId(containers, id)?.lineSpacing,
    // Sert à révéler la structure des lignes imbriquées (cf. renderRows) :
    // les cellules ne prennent leur contour que sur le container en cours.
    isContainerSelected:
      !!ownerContainerId &&
      (selectedContainerIds?.includes(ownerContainerId) ||
        selectedContainer?.id === ownerContainerId),
    hasNotPageNumber,
    containerHeight,
    updateContainer,
    // Le décor de page a son propre réglage des marges, montrées par défaut.
    displayMargins: isPageBackgroundMask(mask)
      ? showsPageBackgroundMargins({ hidden: hidePageBackgroundMargins, fromPdf, fromViewer })
      : displayMargins,
    landscape,
    isHeaderOrFooterMask: mask && (id === MASKS_TYPE.HEADER || id === MASKS_TYPE.FOOTER),
    boxVariableSelectedId,
    fromPdf,
    updateMask
  }
};

const headerSelector = ({ masks }) => getMaskById(masks, MASKS_TYPE.HEADER.id);
const footerSelector = ({ masks }) => getMaskById(masks, MASKS_TYPE.FOOTER.id);

const Column = React.memo(({
  id,
  drawMode,
  canDrop,
  isDragging,
  columnWidth,
  configuration,
  boxes,
  mask,
  isResizable,
  setIsResizingContainer,
  backgroundColor = 'transparent',
  fromViewer,
  columnMaskId,
  linkedMaskHasNotPageNumber,
  rowContainerIds,
  verticalAlign
}) => {
  const header = useStore(headerSelector)
  const footer = useStore(footerSelector)
  const {
    updateContainer,
    containerId,
    containerLineSpacing,
    containerHeight,
    displayMargins,
    landscape,
    isHeaderOrFooterMask,
    fromPdf,
    updateMask,
    hasNotPageNumber,
    isContainerSelected
  } = useStore(selector(id, mask, columnMaskId, linkedMaskHasNotPageNumber, fromViewer), shallow);
  const resizableRef = useRef(null);


  const columnInnerStyle = useMemo(() => {
    // Hors mode dessin la pile de box suit le flux ; elle ne prend un style que
    // pour la répartition « espaces égaux », qui demande toute la hauteur de la
    // cellule (les autres réglages se jouent sur la cellule, cf. ColumnWrapper).
    if (!drawMode)
      return columnStackStyle({ verticalAlign, boxesCount: boxes.length })
    const marginsDisplay = getMarginBackground({
      display: displayMargins,
      configuration,
      landscape,
      // Repères des marges haute et basse d'une page entière : pas sur le décor
      // de page, où les limites de l'en-tête et du pied en tiennent lieu (cf.
      // PageBackgroundFrame).
      showVerticalBorder: !isResizable && !isHeaderOrFooterMask && !isPageBackgroundMask(mask)
    });

    const baseStyle = {
      ...(fromPdf || fromViewer === viewerTypes.DMZ || fromViewer === viewerTypes.PREVIEW ? {} : { outline: `2px ${canDrop || !isDragging ? borderColumnColor : borderColumnErrorColor} dotted` }),
      overflow: 'hidden',
      position: 'absolute',
      width: `100%`,
      maxWidth: `100%`,
      height: "100%",
      color: configuration.baseFontColor?.style,
      transition: 'outline 200ms ease',
      backgroundColor,
      ...marginsDisplay
    }

    if (!mask) return baseStyle
    return { ...baseStyle, ...masksPosition(mask.id, header, footer), }
  }, [
    drawMode,
    displayMargins,
    configuration,
    landscape,
    canDrop,
    isDragging,
    backgroundColor,
    mask,
    isResizable,
    isHeaderOrFooterMask,
    fromPdf,
    header,
    footer,
    fromViewer,
    verticalAlign,
    boxes.length
  ]);

  const onResizeStop = (...args) => {
    setIsResizingContainer(false);
    if (mask) {
      updateMask({
        id: mask.id, fn: (mask, draft) => {
          if (mask.id === footer.id) {
            mask.boxes.forEach((_, idx) => {
              const box = mask.boxes[idx];
              box.top += Math.floor(args[2].clientHeight) - (mask.height ?? MASKS_TYPE.FOOTER.height);
            })
          }

          mask.height = Math.floor(args[2].clientHeight);
          draft.columns.forEach((column) => {
            // Peut être undefined pour une sous-colonne de ligne imbriquée :
            // getColumnAvailableWidth remonte la chaîne des parents de son côté.
            const container = draft.containers.find((c) => c.columnsIds.includes(column.id))
            const newHeader = mask.id === header.id ? mask : header
            const newFooter = mask.id === footer.id ? mask : footer


            if (!column.drawMode) {
              draft.boxes = draft.boxes.map((box) => {
                if (box.columnId === column.id) {
                  return reinitializeBoxWidthAndHeight({
                    box,
                    columnWidth: getColumnAvailableWidth({
                      columnId: column.id,
                      columns: draft.columns,
                      containers: draft.containers,
                      rowContainers: draft.rowContainers,
                      landscape: draft.landscape,
                      marginLeft: draft.configuration.margins.left * gridUnit,
                      marginRight: draft.configuration.margins.right * gridUnit,
                    }),
                    header: newHeader,
                    footer: newFooter,
                  })
                }
                return box
              })
            } else if (container && !column.isOldPage && !column.maskId) {
              if (container.height > maxContainerHeight({ header: newHeader, footer: newFooter })) {
                container.height = maxContainerHeight({ header: newHeader, footer: newFooter })
              }
            }
          })
        }
      })
    }
    else {
      updateContainer({
        id: containerId, fn: (container) => {
          container.height = Math.floor(args[2].clientHeight)
        }
      })
    }
  }

  const maxHeight = () => {
    if (mask && (mask.id === header.id || mask.id === footer.id)) {
      return maxHeaderFooterHeight
    }
    return maxContainerHeight({ header, footer })
  }

  const renderBoxes = () =>
    <div
      id={id}
      style={columnInnerStyle}
      data-role={pageBackground}
      data-parent={containerBackground}
      className={`${drawMode ? 'drawMode' : ''} ${fromPdf && hasNotPageNumber ? 'has-not-page-number' : ''}`}
    >
      {boxes.map(({ id: boxId }) => (
        <BoxWrapper
          id={boxId}
          key={boxId}
          drawMode={drawMode}
          columnWidth={columnWidth}
          fromViewer={fromViewer}
        />
      ))}
      {/* Guides d'alignement : monté ici et pas dans ColumnWrapper pour partager
          exactement le repère des box (left/top relatifs à ce div). Mode dessin
          — colonne ou masque — uniquement, jamais en PDF ni en aperçu. */}
      {drawMode && !fromPdf && !fromViewer && <SnapGuidesOverlay parentId={id} />}
    </div>

  // Colonne transformée en lignes empilées : elle ne porte plus de box en
  // direct, ce sont ses lignes qui portent des sous-colonnes (2 niveaux max).
  if (rowContainerIds?.length) {
    return <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      // Une pile de lignes se lit comme une pile de containers : même écart
      // qu'entre deux containers (« Marge entre les lignes »), que le container
      // peut surcharger pour lui seul depuis le panneau de disposition.
      // L'écart entre colonnes a son propre réglage (container.columnGap) : le
      // piloter avec la même valeur disloquait la grille dès qu'on aérait les
      // lignes.
      rowGap: containerLineSpacing ?? configuration.lineSpacing,
      // Répartition verticale de la colonne : « réparti » étale ses lignes sur
      // toute la hauteur (première en haut, dernière en bas). Les autres
      // réglages placent la pile de lignes en bloc, depuis la cellule.
      ...columnStackStyle({
        verticalAlign,
        boxesCount: rowContainerIds.length,
      }),
    }}>
      {rowContainerIds.map((rowId, rowIndex) => (
        <ColumnsRow
          key={rowId}
          rowContainerId={rowId}
          parentColumnId={id}
          rowIndex={rowIndex}
          isLastRow={rowIndex === rowContainerIds.length - 1}
          parentWidthPx={columnWidth}
          showCellOutline={isContainerSelected}
          fromViewer={fromViewer}
          setIsResizingContainer={setIsResizingContainer}
        />
      ))}
    </div>
  }

  return isResizable ? <Resizable
    ref={resizableRef}
    enable={{
      top: mask && mask.id === footer.id,
      right: false,
      bottom: !mask || mask.id === header.id,
      left: false,
      topRight: false,
      bottomRight: false,
      bottomLeft: false,
      topLeft: false
    }}
    // En-tête / pied de page : la poignée (10 px, à cheval sur le bord par
    // défaut) avait sa moitié extérieure recouverte par la page, qui prenait
    // les clics. On la rentre entièrement dans le masque.
    handleStyles={mask ? { bottom: { bottom: 0 }, top: { top: 0 } } : undefined}
    onResizeStart={() => {
      resizableRef.current.updateSize({
        width: "100%",
        height: containerHeight
      });
      setIsResizingContainer(true)
    }}
    onResizeStop={onResizeStop}
    maxHeight={maxHeight()}
    minHeight={minContainerHeight}
    size={{
      width: "100%"
    }}
  >
    {renderBoxes()}
  </Resizable> : renderBoxes()
})

export default Column
